import { useMemo, useState } from 'react';
import { Box, Button, HStack, Input, Text, Tooltip } from '@chakra-ui/react';
import { RiDownloadLine, RiContactsLine, RiErrorWarningLine } from 'react-icons/ri';

import useAppToast from 'components/toast';
import { DataTable } from '../_ui/DataTable';
import { FilterBar } from '../_ui/FilterBar';
import { StatusBadge } from '../_ui/components';
import { useCampaignUI } from '../_ui/ui';
import { useCampaignRecipientsQuery } from 'api/metaWhatsappApi';
import { downloadRecipients } from '../uploadAudience';
import campaignError from '../campaignErrors';
import { PillSelect, humanState } from '../_ui/controls';
import usePageVisibility from '../hooks/usePageVisibility';
import useDebouncedValue from '../hooks/useDebouncedValue';
import { formatDate } from '../_lib/campaignState';

/**
 * Every contact in this campaign, and exactly where each one stopped.
 *
 * ★ THIS IS THE SCREEN SOMEBODY OPENS WHEN A CUSTOMER SAYS "I NEVER GOT IT".
 * So it shows the failure REASON, not just a red badge — the Meta code and the
 * skip reason are the whole point, and a table that hides them sends the
 * question to an engineer.
 */

const STATES = [
	'QUEUED',
	'SCHEDULED',
	'SENDING',
	'SUBMITTED',
	'SENT',
	'DELIVERED',
	'READ',
	'REPLIED',
	'FAILED',
	'SKIPPED',
	'OPTED_OUT',
	'EXPIRED',
];

/** Why a contact was skipped, in words rather than a database enum. */
const SKIP_REASON = {
	suppressed: 'On the do-not-message list',
	noConsent: 'No consent on record',
	doNotMarket: 'Marked do-not-market',
	badFormat: 'Number could not be read',
	whatsappInvalid: 'Not a WhatsApp number',
	frequencyCapped: 'Messaged too recently',
	alreadyInCampaign: 'Already in this campaign',
	excluded: 'Excluded by hand',
	no_eligible_day: 'No day left in the schedule',
	campaign_cancelled: 'Campaign was cancelled',
	lead_missing: 'The lead was deleted',
	no_variant: 'No message variant',
};

const PAGE_SIZE = 50;
const when = (value) => value ? formatDate(value, true) : '-';

export default function CampaignRecipients({ campaign }) {
	const ui = useCampaignUI();
	const notify = useAppToast();

	const [state, setState] = useState('');
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(1);
	const visible = usePageVisibility();
	const searchValue = useDebouncedValue(search.trim());
	const [downloading, setDownloading] = useState(false);
	const [downloadingProblems, setDownloadingProblems] = useState(false);

	const { data, isLoading, isFetching, isError, refetch } = useCampaignRecipientsQuery({
		id: campaign._id,
		...(state ? { state } : {}),
		...(searchValue ? { search: searchValue } : {}),
		page,
		pageSize: PAGE_SIZE,
	}, { pollingInterval: visible ? (campaign.state === 'PLANNING' || campaign.state === 'RUNNING' ? 10000 : 30000) : 0, refetchOnMountOrArgChange: true });

	const onExport = async ({ problems = false } = {}) => {
		const setBusy = problems ? setDownloadingProblems : setDownloading;
		setBusy(true);
		try {
			const result = await downloadRecipients({
				campaignId: campaign._id,
				campaignName: campaign.name,
				// The error report is deliberately not narrowed by the filter: its
				// whole point is that all four problem states arrive together.
				state: problems ? undefined : state || undefined,
				problems,
			});
			if (!result.ok) notify.error('Export failed', campaignError(result));
		} catch (err) {
			notify.error('Export failed', campaignError(err));
		} finally {
			setBusy(false);
		}
	};

	const columns = useMemo(
		() => [
			{
				key: 'who',
				header: 'Contact',
				cell: (row) => (
					<Box minW='0'>
						<Text fontSize='sm' color={ui.textPrimary}>
							{row.e164 || `+${row.waId}`}
						</Text>
						<Text fontSize='xs' color={ui.textMuted}>
							{row.country || '—'}
							{row.timezone ? ` · ${row.timezone}` : ''}
							{row.source === 'upload' ? ' · uploaded' : ''}
						</Text>
					</Box>
				),
			},
			{
				key: 'state',
				header: 'Status',
				cell: (row) => {
					// ★ The reason, not just the badge.
					const detail =
						row.lastError?.code
							? `WhatsApp error ${row.lastError.code}${row.lastError.title ? ` — ${row.lastError.title}` : ''}`
							: row.skipReason
								? SKIP_REASON[row.skipReason] ?? row.skipReason
								: row.undeliveredAt
									? 'Sent, never confirmed delivered'
									: null;
					return (
						<Box>
							<StatusBadge status={row.state} />
							{detail && (
								<Text fontSize='xs' color={ui.textMuted} mt={1} noOfLines={2}>
									{detail}
								</Text>
							)}
						</Box>
					);
				},
			},
			{
				key: 'variant',
				header: 'Variant',
				cell: (row) => (
					<Text fontSize='sm' color={ui.textSecondary}>
						{row.variantId || '—'}
					</Text>
				),
			},
			{
				key: 'due',
				header: 'Due',
				cell: (row) => (
					<Box>
						<Text fontSize='sm' color={ui.textSecondary}>
							{row.assigned?.day || '—'}
						</Text>
						<Text fontSize='xs' color={ui.textMuted}>
							{when(row.assigned?.releaseAt)}
						</Text>
					</Box>
				),
			},
			{
				key: 'progress',
				header: 'Timeline',
				cell: (row) => (
					<Text fontSize='xs' color={ui.textMuted}>
						{row.submittedAt ? `sent ${when(row.submittedAt)}` : 'not sent'}
						{row.deliveredAt ? ` · delivered` : ''}
						{row.readAt ? ` · read` : ''}
						{row.repliedAt ? ` · replied` : ''}
					</Text>
				),
			},
		],
		[ui],
	);

	return (
		<Box>
			<FilterBar
				px={0}
				py={3}
				trailing={
					<HStack spacing={2}>
						{/* ★ M10. ONE FILE FOR EVERYTHING THAT WENT WRONG. The state
						    filter beside it takes a single value, so building this by
						    hand meant four downloads — and whoever does that forgets
						    EXPIRED, which is the quietest of the four and the one that
						    means the campaign simply ran out of days. */}
						<Tooltip
							hasArrow
							label='Everyone who did not receive the message — failed, skipped, opted out or expired — with the reason for each.'
						>
							<Button
								size='sm'
								leftIcon={<RiErrorWarningLine />}
								variant='neutralOutline'
								borderColor={ui.border}
								color={ui.textPrimary}
								isLoading={downloadingProblems}
								onClick={() => onExport({ problems: true })}
							>
								Problems
							</Button>
						</Tooltip>
						<Button
							size='sm'
							leftIcon={<RiDownloadLine />}
							variant='neutralOutline'
							borderColor={ui.border}
							color={ui.textPrimary}
							isLoading={downloading}
							onClick={() => onExport()}
						>
							Export CSV
						</Button>
					</HStack>
				}
			>
				{/* Twelve states as pills the eye can scan, scrolling sideways on a phone. */}
				<PillSelect
					label='Recipient status'
					value={state}
					onChange={(v) => {
						setState(v);
						setPage(1);
					}}
					options={[{ value: '', label: 'All' }, ...STATES.map((st) => ({ value: st, label: humanState(st) }))]}
					maxW={{ base: '100%', md: '60%' }}
				/>
				<Input
					size='sm'
					maxW='220px'
					placeholder='Search by number' aria-label='Search recipients by number'
					value={search}
					onChange={(e) => {
						setSearch(e.target.value);
						setPage(1);
					}}
					bg={ui.inputBg}
					borderColor={ui.border}
					color={ui.textPrimary}
				/>
			</FilterBar>

			<DataTable
				columns={columns}
				rows={data?.rows ?? []}
				rowKey={(row) => row._id}
				isLoading={isLoading}
				isFetching={isFetching}
				isError={isError}
				onRetry={refetch}
				errorText='We could not load the recipients.'
				emptyState={{
					icon: RiContactsLine,
					title: 'Nobody here yet',
					description: 'Recipients appear once the campaign has been built.',
				}}
				pagination={{
					page,
					total: data?.total ?? 0,
					limit: PAGE_SIZE,
					totalPages: Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE)),
					onPageChange: setPage,
				}}
			/>
		</Box>
	);
}
